// ABOUTME: <jf-hero-randomizer> web component — per-load rotating hero + header re-theming
// (BL-HERO-RANDOM). Ports the live hero-randomizer element: localStorage key
// hero-random-block-{sectionId} advances one block per page load (first visit shows block 0,
// missing/stale stored IDs reset to block 0, index wraps); the chosen slide's data attributes
// re-theme the header. On Horizon the header hooks are #header-component's `transparent`
// attribute (base.css styles body:has(.header[transparent]); when the header is sticky the
// value must be 'not-sticky' so the native stuck-state color revert keeps working), the
// data-jf-hero-theme attribute (text color custom properties in the section stylesheet), the
// data-jf-hero-bg attribute (the slide's background style: on a header that stays in flow a
// dark theme with bg-trans draws live's gradient, sections/jf-header.liquid), and
// the --transparent-header-offset-boolean recompute that theme.liquid/utilities.js only run
// at document load — mirrored here after a runtime toggle. The updated-by-hero guard is
// kept: only the first randomizer on a page touches the header. When the section's inline
// chooser has already rotated and stamped data-jf-chosen (it runs while the document is still
// parsing so the chosen image starts loading early), the element shows that slide and does not
// rotate again.

class JfHeroRandomizer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.header = document.getElementById('header-component');
    this.randomBlocks = (this.dataset.blockIds || '').split(',').filter(Boolean);
    this.heroItems = [...this.querySelectorAll('[data-hero-random-item]')];
    this.heroItemContent = [...this.querySelectorAll('[data-hero-random-item-content]')];

    if (this.randomBlocks.length === 0) return;
    const chosen = this.dataset.jfChosen;
    if (chosen && this.randomBlocks.includes(chosen)) {
      this.showHeroBlock(chosen);
    } else {
      this.rotate();
    }
    this.bindEvents();
  }

  bindEvents() {
    this.heroItemContent.forEach((content) => {
      content.addEventListener('mouseenter', (event) => {
        event.target.closest('[data-hero-random-item]')?.classList.add('show-description');
      });
      content.addEventListener('mouseleave', (event) => {
        event.target.closest('[data-hero-random-item]')?.classList.remove('show-description');
      });
    });
  }

  rotate() {
    const key = `hero-random-block-${this.sectionId}`;
    let nextBlockId;
    let stored = null;
    try {
      stored = localStorage.getItem(key);
    } catch (error) {
      // Storage unavailable (privacy mode): show block 0, no persistence.
    }

    if (stored == null) {
      nextBlockId = this.randomBlocks[0];
    } else {
      const index = this.randomBlocks.indexOf(stored);
      let nextIndex = index + 1;
      if (nextIndex >= this.randomBlocks.length) nextIndex = 0;
      nextBlockId = this.randomBlocks[nextIndex];
    }

    try {
      localStorage.setItem(key, nextBlockId);
    } catch (error) {
      // Ignore: rotation still shows a slide this load, it just cannot advance next load.
    }
    this.showHeroBlock(nextBlockId);
  }

  showHeroBlock(id) {
    const target = this.querySelector(`#jf-hero-block-${CSS.escape(id)}`);
    if (target == null) return;

    this.applyHeaderTheme(target);
    this.heroItems.forEach((item) => {
      item.classList.add('jf-hero-item--hidden');
    });
    target.classList.remove('jf-hero-item--hidden', 'jf-hero-item--invisible');
  }

  applyHeaderTheme(target) {
    if (!this.header) return;
    // The slide's header settings apply only when this randomizer is the first section on the
    // page (the setting's own promise): a randomizer placed lower never claims the header.
    if (this.dataset.firstSection !== 'true') return;
    const { blockHeaderTheme, blockBgStyle, applyBlockSettings } = target.dataset;

    // Only index and page templates let a transparent slide pull the page under the header
    // (the live layout's is_trans_header gate); elsewhere the header stays in flow.
    const transparentAllowed = this.dataset.transparentHeaderAllowed === 'true';

    if (applyBlockSettings === 'true' && !this.header.hasAttribute('data-jf-updated-by-hero')) {
      if (blockBgStyle === 'bg-trans' && transparentAllowed) {
        const value = this.header.hasAttribute('sticky') ? 'not-sticky' : 'always';
        // Header measurement is opt-in (layout/theme.liquid); the transparent offset rules consume it.
        window.measureHeaderHeights?.();
        this.header.setAttribute('transparent', value);
      } else {
        this.header.removeAttribute('transparent');
      }

      if (blockHeaderTheme) {
        this.header.setAttribute('data-jf-hero-theme', blockHeaderTheme);
      }

      if (blockBgStyle) {
        this.header.setAttribute('data-jf-hero-bg', blockBgStyle);
      }

      this.updateTransparentHeaderOffset();
    }

    this.header.setAttribute('data-jf-updated-by-hero', '');
  }

  // Mirrors utilities.js updateTransparentHeaderOffset(), which only runs at document load —
  // a runtime transparent toggle must recompute the offset boolean itself.
  updateTransparentHeaderOffset() {
    const headerGroup = document.querySelector('#header-group');
    const headerSection = headerGroup?.querySelector('.header-section');
    if (!headerSection || !this.header?.hasAttribute('transparent')) {
      document.body.style.setProperty('--transparent-header-offset-boolean', '0');
      return;
    }
    const hasImmediateSection = headerSection.nextElementSibling?.classList.contains('shopify-section');
    document.body.style.setProperty('--transparent-header-offset-boolean', hasImmediateSection ? '0' : '1');
  }
}

if (!customElements.get('jf-hero-randomizer')) {
  customElements.define('jf-hero-randomizer', JfHeroRandomizer);
}
